import {
  addDays,
  addMinutes as addMinutesDate,
  format,
  parseISO,
  isBefore as isBeforeFn,
  isAfter as isAfterFn,
  startOfWeek,
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { z } from 'zod';

export const DayName = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayName = (typeof DayName)[number];

export const DayNameCopy = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
} as const satisfies { [k in DayName]: Day };

export const DayCopy = {
  Monday: 'mon',
  Tuesday: 'tue',
  Wednesday: 'wed',
  Thursday: 'thu',
  Friday: 'fri',
  Saturday: 'sat',
  Sunday: 'sun',
} as const satisfies { [k in Day]: DayName };

export const Day = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export type Day = (typeof Day)[number];

export type TimeOfWeek = number & {
  _TimeOfWeek: 'TimeOfWeek';
};

export const ZodTimeOfDay = z
  .string()
  .refine((s) => {
    const [hours, minutes] = s.split(':');
    return !(isNaN(hours as unknown as number) || isNaN(minutes as unknown as number));
  })
  .brand('TimeOfDay');

export type TimeOfDay = z.infer<typeof ZodTimeOfDay>;

export type Timestamp = string & {
  _timestampBrand: 'Timestamp';
};
export const ZodTimestamp = z
  .string()
  .refine((s) => parseISO(s), 'Not a valid ISO timestamp')
  .transform((s) => s as Timestamp);

export type DateOnly = string & {
  _dateOnlyBrand: 'DateOnly';
};

export const ZodDateOnly = z
  .string()
  .or(z.date())
  .refine((s) => (typeof s === 'string' ? parseISO(s) : true), 'Not a valid date string')
  .transform((s) => {
    const dateString = (typeof s === 'string' ? parseISO(s) : s).toISOString();
    return dateString.split('T')[0] as DateOnly;
  });

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
export const now = (): Timestamp => new Date().toISOString() as Timestamp;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
export const timestamp = (date: Date): Timestamp => date.toISOString() as Timestamp;

export const formatTime = (minutes: number, day: DayName) => {
  const dayOffset = DayName.indexOf(day) * 24 * 60;
  const normalizedMinutes = minutes - dayOffset;
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};
export const timeOfWeekToTimeString = (timeOfWeek: number): string => {
  const dayOffset = Math.floor(timeOfWeek / (24 * 60));
  const minutesInDay = timeOfWeek - dayOffset * 24 * 60;
  const hour = Math.floor(minutesInDay / 60);
  const minute = minutesInDay % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

export const dayAndMinutesToTimeOfWeek = (day: DayName, minutesInDay: number): TimeOfWeek => {
  const dayIndex = DayName.indexOf(day);
  const minutes = dayIndex * 24 * 60 + minutesInDay;
  return minutes as TimeOfWeek;
};

export const getCurrentDay = (day = new Date()) => {
  const pacificDate = toZonedTime(day, 'America/Los_Angeles');
  return format(pacificDate, 'EEEE') as Day;
};

export const getCurrentDayName = (day = new Date()) => {
  const currentDay = getCurrentDay(day);
  return DayCopy[currentDay];
};

export const formatTimeStringFromTimeOfWeek = (
  timeOfWeek: TimeOfWeek,
  includeZone = false,
  short = false,
  useUserTimeZone = true,
) => {
  const pacificNow = toZonedTime(new Date(), 'America/Los_Angeles');

  const dowFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
  });
  const weekdayShort = dowFormatter.format(pacificNow).toLowerCase();
  const dayIndexMap: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };
  const currentDow = dayIndexMap[weekdayShort];
  const deltaFromMonday = (currentDow + 6) % 7; // 0 for Monday, ... 6 for Sunday

  const mondayInstant = addDays(pacificNow, -deltaFromMonday);

  const ymdParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(mondayInstant); // YYYY-MM-DD in en-CA

  const mondayMidnightUtc = fromZonedTime(`${ymdParts}T00:00:00`, 'America/Los_Angeles');
  const utcInstant = addMinutesDate(mondayMidnightUtc, timeOfWeek);

  return new Intl.DateTimeFormat('en-US', {
    hour12: true,
    hour: 'numeric',
    timeZone: useUserTimeZone ? undefined : 'America/Los_Angeles',
    ...(short ? {} : { minute: 'numeric' }),
    ...(includeZone ? { timeZoneName: 'short' as const } : {}),
  }).format(utcInstant);
};

export const formatShowTimeFromTimeOfWeek = (
  times: {
    start_time: TimeOfWeek;
    end_time: TimeOfWeek;
  },
  short = false,
) =>
  `${formatTimeStringFromTimeOfWeek(times.start_time, false, short)} - ${formatTimeStringFromTimeOfWeek(times.end_time, true, short)}`;

export const getMinutesSinceStartOfWeek = (date = new Date()): TimeOfWeek => {
  const pacificDate = toZonedTime(date, 'America/Los_Angeles');
  const weekStart = startOfWeek(pacificDate, { weekStartsOn: 1 });
  const diffInMinutes = Math.floor((pacificDate.getTime() - weekStart.getTime()) / (1000 * 60));
  return diffInMinutes as TimeOfWeek;
};

export const isBefore = (a: Timestamp, b: Timestamp) => isBeforeFn(parseISO(a), parseISO(b));
export const isAfter = (a: Timestamp, b: Timestamp) => isAfterFn(parseISO(a), parseISO(b));

export const formatTimestampTime = (t: Timestamp, useUserTimeZone = true, showTimeZone = true) => {
  const timeZone = useUserTimeZone ? undefined : 'America/Los_Angeles';

  // Format the time
  const timeStr = new Intl.DateTimeFormat('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
    timeZone,
  }).format(parseISO(t));

  if (!showTimeZone) { return timeStr; }

  // Get the CURRENT timezone offset (not the historical one)
  const currentTzStr = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    timeZone,
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;

  return `${timeStr} ${currentTzStr}`;
};
export const formatTimestampDay = (t: Timestamp, withDayOfWeek = false, withYear = true) => {
  let formatStr = '';
  if (withDayOfWeek) {
    formatStr += 'EEE, ';
  }
  formatStr += 'MMM d';
  if (withYear) {
    formatStr += ', yyyy';
  }
  return format(parseISO(t), formatStr);
};

export const formatTimestampDayWithDayOfWeekNoYear = (t: Timestamp) =>
  format(parseISO(t), 'EEEE, MMM d');

export const addMinutes = (a: Timestamp, b: number) => timestamp(addMinutesDate(parseISO(a), b));
